import { getBranding, type Branding } from './branding'
import { parseColor, derivePrimaryScale } from './primaryScale'

const TOKEN_VARS: Array<[keyof Branding, string]> = [
  ['brandPrimary', '--brand-primary'],
  ['brandAccent', '--brand-accent'],
  ['brandInk', '--brand-ink'],
  ['brandTint', '--brand-tint'],
  ['brandWash', '--brand-wash'],
  ['brandBorder', '--brand-border'],
]

/**
 * Apply per-instance brand color tokens as CSS variables on :root. Called once
 * at boot. Absent tokens are left untouched so the globals.css defaults (the
 * app's blue primary) apply — an un-branded deployment is unchanged.
 */
export function applyBrandTheme(branding: Branding = getBranding()): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  for (const [key, cssVar] of TOKEN_VARS) {
    const value = branding[key]
    if (typeof value === 'string' && value.trim()) {
      root.style.setProperty(cssVar, value.trim())
    }
  }

  // App chrome scale — separate from the --brand-* accents above. When
  // APP_PRIMARY is set on the deployment, derive a full 50-900 scale from it
  // and override the --primary-* vars backing the Tailwind `primary` palette
  // (see the @theme block in src/styles/globals.css). Invalid values are
  // ignored so a bad env var can't break boot styling.
  if (typeof branding.appPrimary === 'string') {
    const base = parseColor(branding.appPrimary)
    if (base) {
      const scale = derivePrimaryScale(base)
      for (const [step, triplet] of Object.entries(scale)) {
        root.style.setProperty(`--primary-${step}`, triplet)
      }
      // basis-brandable semantic vars (adoption Phase 1, theming Option A —
      // see docs/superpowers/specs/2026-07-14-basis-design-system-adoption-assessment.md §5).
      // Mode-neutral 500 stop for continuity with the legacy chrome; Phase 4
      // replaces this with basis's per-mode stop mapping + WCAG clamp.
      root.style.setProperty('--primary', `rgb(${scale[500]})`)
      root.style.setProperty('--primary-foreground', 'rgb(255 255 255)')
      root.style.setProperty('--sidebar-primary', `rgb(${scale[500]})`)
      root.style.setProperty('--sidebar-primary-foreground', 'rgb(255 255 255)')
    }
  }
}
